import type { CreateInstrumentItem, InstrumentProduct, RateType } from '../../../services/fundingApi';
import { formatDateFromBRToISO } from '../../../utils/DateUtils';
import { currencyToNumber, currencyToNumberUs, parseAmbiguousDecimal } from '../../../utils/currencyUtils';

// Módulo puro (sem React/Amplify) com a lógica de parsing de "Lançar
// Emissões" (colar planilha) - extraído de IssuanceFormPage.tsx pra poder ser
// testado com Jest sem carregar a cadeia de imports pesada do componente
// (fundingApi.ts -> aws-amplify, que quebra o parser do Jest fora de um
// ambiente de teste de componente). Ver IssuanceFormPage.test.ts.

// Mapeamento de colunas por corretora ao colar planilhas. Cada corretora
// exporta a emissão com nomes de coluna diferentes; este mapa traduz "NOME DA
// COLUNA NA PLANILHA DA CORRETORA" (em caixa alta) -> nome do campo do
// payload de POST /instruments (docs/api-contract.md). Conhecimento de
// negócio validado com a Tesouraria - não inventado.
//
// Indexado pelo NOME da plataforma (ver GET /platforms), não pelo id - é só
// uma ajuda de parsing local, o backend não sabe desse mapeamento. As chaves
// AGORA e NUBANK foram ajustadas para bater com a grafia real retornada por
// GET /platforms (seed de 2026-08-18: AGORA sem acento, NUBANK por extenso -
// o mapeamento antigo usava "ÁGORA"/"NU"). "GENIAL" e "GENIAL INST" são
// corretoras DISTINTAS (contas CETIP e CNPJs diferentes, confirmado com o
// usuário em 2026-09-01) - não confundir as amostras uma com a outra.
//
// Campo "indexador_raw" recebe o texto crú da coluna de índice da corretora
// (ex: "PRE", "DI", "CDI", "PRÉ-FIXADO") - normalizeRateAndIndex() abaixo
// deriva rate_type (PRE/POS) e remuneration_index a partir dele no submit.
//
// Amostras reais recebidas em 2026-08-19 (Toro, Daycoval, Genial, BTG, PicPay)
// confirmaram os campos novos (issue_date, pu, financial_value, di_futuro) para
// essas 5 corretoras - ver docs/context/business-context.md secao 8.5. Para as
// corretoras sem amostra nova (XP, AGORA, ITAU, MIRAE) só o indexador
// foi renomeado para indexador_raw; pu/financial_value/issue_date continuam
// sem mapeamento conhecido para elas (ficam para arrastar/preencher
// manualmente até termos uma amostra real de cada uma). NIKOS tem amostra real
// mas não é tabular - ver parseNikosBlock mais abaixo, não usa este mapa.
export const BROKER_FIELD_MAPPING: Record<string, Record<string, string>> = {
    DAYCOVAL: {
        'TIPO IF': 'product',
        'VENCIMENTO': 'due_date',
        'INDEXADOR': 'indexador_raw',
        'TAXA': 'tax',
        // Taxa cheia/distribuidor - mapeada em 2026-09-02 pra alimentar a
        // conferência de PU Desagiado (duas taxas) - ver
        // docs/context/business-context.md §8.9.
        'TAXA DISTRIBUIDOR': 'tax_gross',
        'QUANTIDADE': 'quantity',
        'PU': 'pu',
        'PU DESAGIADO': 'pu_degassed',
        'VALOR': 'financial_value',
        'PREXDI': 'di_futuro',
    },
    // Amostra real 2026-08-31 (todas as linhas PRE): Emissor, Ativo, Indexador,
    // Emissão, Prazo Carência, Carência, Prazo (DC), Dt. Vencimento, Taxa XP,
    // Taxa Cliente, Taxa Pré (a.a.), Quantidade, PU, Financeiro, Taxa
    // Pré-Fixada XP, Taxa Pré-Fixada Cliente, Periodicidade de Juros. Pra papel
    // PRE, "Taxa XP"/"Taxa Cliente" vêm zeradas e "Taxa Pré-Fixada Cliente" é a
    // taxa cliente de fato - qual coluna vale pra papel POS ainda não foi
    // confirmado (sem amostra POS da XP até agora).
    // A XP manda só UMA coluna de PU (bate exatamente com Financeiro = Quantidade
    // × PU) - sem PU de emissão (cheio) separado, diferente de Toro/Genial/
    // Daycoval/BTG. Mapeada pra pu_degassed; pu (nominal) fica sem coluna e cai
    // no default de 1000 em toCreateItem (ver DEFAULT_PU_NOMINAL) - decisão
    // tomada em 2026-08-31 sabendo que isso pode gerar alerta de divergência no
    // check de PU Desagiado pra papéis PRE de prazo longo (é só aviso, não
    // bloqueia o cadastro - ver docs/context/business-context.md §8.2).
    XP: {
        'ATIVO': 'product',
        'EMISSÃO': 'issue_date',
        'DT. VENCIMENTO': 'due_date',
        'INDEXADOR': 'indexador_raw',
        'TAXA PRÉ-FIXADA CLIENTE': 'tax',
        // Taxa cheia/da própria XP - mapeada em 2026-09-02 pra alimentar a
        // conferência de PU Desagiado (duas taxas) - ver
        // docs/context/business-context.md §8.9.
        'TAXA PRÉ-FIXADA XP': 'tax_gross',
        'QUANTIDADE': 'quantity',
        'PU': 'pu_degassed',
        'FINANCEIRO': 'financial_value',
    },
    // Amostra real 2026-08-19: Emissor, Ativo, Indexador, Prazo, Data Vencimento,
    // Taxa Emissor, Taxa Cliente, PU Emissão, Quantidade, Valor Aplicação,
    // Liquidez, PU Desagiado, CDI. Sem coluna de data de emissão - issue_date
    // cai no default (hoje), editável depois (ver toCreateItem).
    TORO: {
        'ATIVO': 'product',
        'DATA VENCIMENTO': 'due_date',
        'INDEXADOR': 'indexador_raw',
        'TAXA CLIENTE': 'tax',
        // Taxa cheia/emissor - mapeada em 2026-09-02 pra alimentar a
        // conferência de PU Desagiado (duas taxas) - ver
        // docs/context/business-context.md §8.9.
        'TAXA EMISSOR': 'tax_gross',
        'PU EMISSÃO': 'pu',
        'QUANTIDADE': 'quantity',
        'VALOR APLICAÇÃO': 'financial_value',
        'PU DESAGIADO': 'pu_degassed',
        'CDI': 'di_futuro',
    },
    // IMPORTANTE (confirmado com o usuário em 2026-09-01): "Toro" é o nome
    // ANTIGO desta mesma corretora - mesma conta CETIP (26886002), mesmo
    // formato de planilha/e-mail. Não existe (e nunca existiu) uma Platform
    // "TORO" cadastrada no banco - amostras rotuladas como "Toro" nesta ou
    // em sessões futuras são a SANTCOR, não uma corretora nova. O
    // mapeamento abaixo (herdado da TORO de 2026-08-19) já cobre as duas.
    //
    // Amostra real 2026-09-01 (10 linhas, papéis PRE e DI/POS), colada no
    // corpo de um e-mail (não planilha) - header IDÊNTICO ao da TORO acima
    // (Emissor, Ativo, Indexador, Prazo, Data Vencimento, Taxa Emissor, Taxa
    // Cliente, PU Emissão, Quantidade, Valor Aplicação, Liquidez, PU
    // Desagiado, CDI), incluindo a coluna extra de numeração de linha (1, 2,
    // 3...) que o CopyPasteTable já ignora por não bater com nenhum header
    // conhecido. Reaproveitado o mesmo mapeamento da TORO - confirmado
    // matematicamente que Valor Aplicação = Quantidade × PU Emissão (nominal
    // 1000), não PU Desagiado (ex: linha 1: 4 × 1000,00 = R$ 4.000,00).
    // Números em formato BR normal (vírgula decimal, ex: "12,50%",
    // "997,33214639", "R$ 4.000,00") - currencyToNumber padrão já dá conta
    // (sem o bug de formato americano da XP/PicPay). Datas em DD/MM/AAAA
    // normal. Sem coluna de data de emissão - cai no default de hoje, mesmo
    // padrão da TORO. Amostra extra de 3 linhas (2026-09-01, rotulada "Toro"
    // pelo usuário) confirmou também o produto CDB (além de LCA), mesma
    // conta CETIP, sem mudança de mapeamento necessária.
    SANTCOR: {
        'ATIVO': 'product',
        'DATA VENCIMENTO': 'due_date',
        'INDEXADOR': 'indexador_raw',
        'TAXA CLIENTE': 'tax',
        // Taxa cheia/emissor - mapeada em 2026-09-02 pra alimentar a
        // conferência de PU Desagiado (duas taxas) - ver
        // docs/context/business-context.md §8.9.
        'TAXA EMISSOR': 'tax_gross',
        'PU EMISSÃO': 'pu',
        'QUANTIDADE': 'quantity',
        'VALOR APLICAÇÃO': 'financial_value',
        'PU DESAGIADO': 'pu_degassed',
        'CDI': 'di_futuro',
    },
    // Amostra real 2026-08-19 tinha uma coluna "FINANCEIRO" duplicada (bruta e
    // desagiada, mesmo nome) - resolvido em 2026-08-19 sem precisar confirmar
    // com a Genial: dá pra deduzir matematicamente qual é qual (verificado nas
    // 3 linhas da amostra) - QTDE × PU DE EMISSÃO bate exatamente com a 1ª
    // ocorrência de "FINANCEIRO" (ex: 73 × 1000 = 73.000,00) e QTDE × PU
    // DESAGIADO bate com a 2ª (ex: 73 × 999,06372357 = 72.931,65 - valor
    // desagiado, que nem pra Daycoval mapeamos). CopyPasteTable agora usa a
    // 1ª ocorrência de cada nome de coluna (fix nesta mesma revisão), então
    // mapear "FINANCEIRO" -> financial_value pega a bruta, correta.
    // "COMANDO" (número de controle da própria Genial) continua descartado -
    // decisão de negócio de 2026-08-19, ver business-context.md §8.5.
    // issue_date ainda não tem coluna confirmada (o cabeçalho colado tinha uma
    // coluna sem nome reconhecível ali) - mas como todas as linhas da amostra
    // tinham a mesma data (a do lote/lançamento), o default de "hoje" já usado
    // por Toro/Daycoval/INTER produz o mesmo resultado prático na maioria dos
    // casos - não é mais tratado como bloqueio, só uma lacuna de precisão.
    //
    // ATENÇÃO: "GENIAL" (esta chave) é uma conta CETIP diferente de "GENIAL
    // INST" (ver abaixo) - CNPJ/corretora distintos (17372006 vs 00453004 no
    // seed de plataformas), confirmado com o usuário em 2026-09-01. Não
    // confundir as amostras das duas - o mapeamento abaixo é da Genial
    // "normal" (2026-08-19), não da Institucional.
    GENIAL: {
        'EMISSÃO': 'product',
        'VENCTO': 'due_date',
        'ÍNDICE': 'indexador_raw',
        'TAXA DE EMISSÃO': 'tax',
        'QTDE': 'quantity',
        'PU DE EMISSÃO': 'pu',
        'FINANCEIRO': 'financial_value',
        'PU DESAGIADO': 'pu_degassed',
        'CURVA': 'di_futuro',
    },
    // Amostra real 2026-09-01 (6 linhas, todas PRE), colada no corpo de um
    // e-mail - corretora DISTINTA da "GENIAL" acima (conta CETIP 00453004,
    // "GENIAL INSTITUCIONAL CCTVM S.A.", confirmado pelo usuário; a "GENIAL"
    // simples é 17372006, entidade diferente). Bug real reportado pelo
    // negócio como "Genial inst – ordem do cabeçalho errada" -
    // **inicialmente corrigido por engano na chave GENIAL (revertido) antes
    // de perceber que era essa corretora aqui**.
    //
    // Header tem 14 colunas nomeadas + 1 coluna de Emissor SEM nome no
    // cabeçalho na frente (mesmo padrão do SANTCOR - "Agrolend", constante
    // em toda linha) - 15 valores por linha de dado. Essa é a mesma coluna
    // sem nome que a sessão de 2026-08-19 já tinha detectado na Genial
    // "normal" sem saber o que era - agora confirmado (nesta corretora, ao
    // menos) que é o Emissor, não issue_date.
    // - "TÍTULO" é o PRODUTO ("LCA").
    // - "EMISSÃO" é a DATA DE EMISSÃO ("31/08/2026").
    // - "TAXA CHEIA" (maior, ex: 13,35) é a taxa cheia/emissor - não
    //   mapeada; "TAXA DE EMISSÃO" (menor, ex: 12,95) é a taxa de CLIENTE,
    //   apesar do nome sugerir o contrário - confirmado por ser sempre a
    //   menor das duas (mesmo padrão cliente <= emissor de outras
    //   corretoras).
    // Números em formato BR normal (vírgula decimal, sem "%" - ex: "13,35",
    // não "13,35%"), currencyToNumber padrão já dá conta. Datas em
    // DD/MM/AAAA normal. "CURVA" manda "0" literal em papel PRE (mesmo
    // mecanismo de normalização usado pra "GENIAL" e Daycoval/"NaN").
    'GENIAL INST': {
        'TÍTULO': 'product',
        'EMISSÃO': 'issue_date',
        'VENCTO': 'due_date',
        'ÍNDICE': 'indexador_raw',
        'TAXA DE EMISSÃO': 'tax',
        // Mapeada em 2026-09-02 pra alimentar a conferência de PU Desagiado
        // (duas taxas) - ver docs/context/business-context.md §8.9.
        'TAXA CHEIA': 'tax_gross',
        'QTDE': 'quantity',
        'PU DE EMISSÃO': 'pu',
        'FINANCEIRO': 'financial_value',
        'PU DESAGIADO': 'pu_degassed',
        'CURVA': 'di_futuro',
    },
    // NIKOS não entra aqui: não manda planilha tabular (cabeçalho+linhas) - manda
    // uma "memória de cálculo" por operação, em células fixas (confirmado em 5
    // arquivos reais, 2026-08-31/2026-09-02). Ver parseNikosBlock mais abaixo
    // neste arquivo (colagem em bloco, não colunar).
    AGORA: {
        'ATIVO': 'product',
        'VCTO': 'due_date',
        'INDICE': 'indexador_raw',
        'TX EMISSÃO': 'tax',
        'QTDE': 'quantity',
        'PU DESÁGIO': 'pu_degassed',
    },
    // Colunas reais confirmadas no bug do produto "CDB PRE" (2026-08-19): TIPO DE
    // CONTA, CONTA, ATIVO, EMISSOR, PRAZO/VENCIMENTO, FINANCEIRO, PU, QUANTIDADE,
    // TAXA CLIENTE (EMISSÃO), TAXA INTER DTVM (CHEIA), PU Desagiado, INDEXADOR,
    // DIAS C, DIAS U, DI, BROKER. Sem coluna de data de emissão.
    INTER: {
        'ATIVO': 'product',
        'PRAZO / VENCIMENTO': 'due_date',
        'INDEXADOR': 'indexador_raw',
        'TAXA CLIENTE (EMISSÃO)': 'tax',
        // Taxa cheia/DTVM - mapeada em 2026-09-03 a partir de amostra real (nunca
        // tinha sido conferida antes) pra alimentar a conferência de PU Desagiado
        // (duas taxas) - ver docs/context/business-context.md §8.9.
        'TAXA INTER DTVM (CHEIA)': 'tax_gross',
        'PU': 'pu',
        'QUANTIDADE': 'quantity',
        'FINANCEIRO': 'financial_value',
        'PU DESAGIADO': 'pu_degassed',
        'DI': 'di_futuro',
    },
    ITAU: {
        'PRODUTO': 'product',
        'DATA VENCIMENTO': 'due_date',
        'INDEXADOR': 'indexador_raw',
        'TAXA CLIENTE': 'tax',
        'QUANTIDADE': 'quantity',
        'PU DESAGIADO': 'pu_degassed',
    },
    // Amostra real 2026-08-19: Produto, Prazo (Mês), Data Emissão, Data
    // Vencimento, DC, DU, Liquidez, Data de Início da Liquidez, Indexador,
    // Curva DI, Fee (a.a.), Taxa All, Taxa Cliente, Quantidade, PU Emissão,
    // Emissão (R$), PU Desagiado. Nomes de coluna confirmados de novo com
    // amostra completa de 6 linhas em 2026-09-01 - mapeamento em si (nomes)
    // já estava certo, mas achado bug real de FORMATO DE NÚMERO por coluna
    // (ver US_NUMBER_FORMAT_FIELDS): "CURVA DI" e "EMISSÃO (R$)" vêm em
    // formato americano (ex: "14.49%", "75,000.00") dentro da MESMA
    // planilha onde "TAXA CLIENTE"/"PU EMISSÃO"/"PU DESAGIADO" vêm em
    // formato BR normal (ex: "99,00%", "1.000,00", "995,98186417") -
    // confirmado matematicamente que Quantidade × PU Emissão bate com
    // Emissão (R$) só lendo essa coluna como americana.
    BTG: {
        'PRODUTO': 'product',
        'DATA EMISSÃO': 'issue_date',
        'DATA VENCIMENTO': 'due_date',
        'INDEXADOR': 'indexador_raw',
        'CURVA DI': 'di_futuro',
        'TAXA CLIENTE': 'tax',
        // Taxa cheia/all-in (sempre 100,00% nas amostras vistas) - mapeada em
        // 2026-09-02 pra alimentar a conferência de PU Desagiado (duas
        // taxas) - ver docs/context/business-context.md §8.9.
        'TAXA ALL': 'tax_gross',
        'QUANTIDADE': 'quantity',
        'PU EMISSÃO': 'pu',
        'EMISSÃO (R$)': 'financial_value',
        'PU DESAGIADO': 'pu_degassed',
    },
    // Duas fontes reais pra mesma corretora, cabeçalhos diferentes - confirmado
    // em 2026-09-02 com a MESMA amostra (20 linhas, PRE e POS/CDI) recebida tanto
    // colada no corpo do e-mail (ATIVO/TX EMISSOR/TX CLIENTE/QTDE) quanto no
    // Excel anexo (TITULO/TAXA EMISSOR/TAXA CLIENTE/QTDE TOTAL) - por isso as
    // duas grafias de cada campo aparecem aqui. O mapeamento antigo só cobria a
    // variante do Excel e não tinha tax_gross/di_futuro/PU EMISSAO (pu nominal)
    // - nenhuma amostra real tinha sido conferida antes.
    NUBANK: {
        'ATIVO': 'product',
        'TITULO': 'product',
        'VCTO': 'due_date',
        'INDICE': 'indexador_raw',
        'TX CLIENTE': 'tax',
        'TAXA CLIENTE': 'tax',
        // Taxa cheia/emissor - mapeada em 2026-09-02 pra alimentar a conferência
        // de PU Desagiado (duas taxas) - ver docs/context/business-context.md §8.9.
        'TX EMISSOR': 'tax_gross',
        'TAXA EMISSOR': 'tax_gross',
        'QTDE': 'quantity',
        'QTDE TOTAL': 'quantity',
        // Taxa de referência (CDI) do prazo, presente em toda linha - inclusive
        // papel Pré (informativo; a conferência de PU Desagiado só usa di_futuro
        // pra papel POS, ignora pra PRE - ver check_pu_desagiado.py no backend).
        'DI': 'di_futuro',
        'PU': 'pu_degassed',
        'PU EMISSAO': 'pu',
    },
    MIRAE: {
        'ATIVO': 'product',
        'VENCIMENTO': 'due_date',
        'INDEXADOR': 'indexador_raw',
        'TAXA CLIENTE': 'tax',
        'QUANTIDADE': 'quantity',
        'PU': 'pu_degassed',
    },
    // Amostra real 2026-09-01 (arquivo completo, não só a amostra de
    // 2026-08-19): Emissor, Ativo, Indexador, Emissão, Prazo Carência,
    // Carência, Prazo (DC), Dt. Vencimento, Taxa, Taxa Cliente, Quantidade,
    // PU, Financeiro. A PicPay manda só UMA coluna de PU (bate exatamente com
    // Financeiro = Quantidade × PU, conferido linha a linha) - é o PU
    // desagiado, não o nominal, mesmo padrão da XP (ver comentário lá em
    // cima). "PU" mapeado pra pu_degassed; pu (nominal) fica sem coluna e cai
    // no default de 1000 em toCreateItem. Mapeamento anterior (2026-08-19)
    // tinha isso invertido ('PU': 'pu') com base numa amostra menor onde a
    // conta não fechava - corrigido em 2026-09-01 com o arquivo real completo.
    PICPAY: {
        'ATIVO': 'product',
        'EMISSÃO': 'issue_date',
        'DT. VENCIMENTO': 'due_date',
        'INDEXADOR': 'indexador_raw',
        'TAXA CLIENTE': 'tax',
        // Taxa cheia (coluna "Taxa", distinta de "Taxa Cliente") - mapeada em
        // 2026-09-02 pra alimentar a conferência de PU Desagiado (duas
        // taxas) - ver docs/context/business-context.md §8.9.
        'TAXA': 'tax_gross',
        'QUANTIDADE': 'quantity',
        'PU': 'pu_degassed',
        'FINANCEIRO': 'financial_value',
    },
};

// PicPay envia issue_date em M/D/AAAA (US) mas due_date em DD/MM/AAAA (BR) -
// formato varia por COLUNA dentro da mesma planilha, não por corretora como
// um todo (confirmado inspecionando o number_format real das células do
// Excel: "Emissão" usa "mm-dd-yy", "Dt. Vencimento" usa "dd/mm/yyyy"). Por
// isso a chave é "PLATAFORMA:campo", não só a plataforma - ver toCreateItem.
export const US_DATE_FORMAT_FIELDS = new Set(['PICPAY:issue_date']);

// XP e PicPay exportam números em formato americano (vírgula = milhar, ponto
// = decimal - ex: "66,646.75", "12.70", "998.0046553") em vez do formato
// brasileiro ("66.646,75", "12,70") usado pelas demais corretoras - PicPay
// confirmado em 2026-09-01 (mesmo bug da XP: sem isso o parser BR remove o
// ponto como se fosse separador de milhar, e "998.0046553" vira 9980046553).
// Datas não seguem essa mesma flag (ver US_DATE_FORMAT_FIELDS acima - formato
// de número e de data variam independente um do outro, inclusive por coluna
// dentro da mesma corretora). Ver currencyToNumberUs em utils/currencyUtils.ts.
// NIKOS não usa BROKER_FIELD_MAPPING (colagem tabular) - usa parseNikosBlock
// (ver rodapé do arquivo), que já produz os campos numéricos sempre em ponto
// decimal (nunca vírgula), então cai no mesmo parser da XP/PicPay.
export const US_NUMBER_FORMAT_PLATFORMS = new Set(['XP', 'PICPAY', 'NIKOS']);

// BTG mistura formato de número DENTRO da mesma planilha - "CURVA DI" e
// "EMISSÃO (R$)" vêm em formato americano (ex: "14.49%", "75,000.00")
// enquanto "TAXA CLIENTE"/"PU EMISSÃO"/"PU DESAGIADO" vêm em formato BR
// normal (ex: "99,00%", "1.000,00") - confirmado matematicamente em
// 2026-09-01 (Quantidade × PU Emissão só bate com Emissão (R$) lendo essa
// coluna como americana). Diferente de US_NUMBER_FORMAT_PLATFORMS (que vale
// pra TODOS os campos numéricos de uma plataforma), aqui o formato varia por
// CAMPO - mesma ideia de US_DATE_FORMAT_FIELDS, mas pra número. Checado em
// toCreateItem antes do flag de plataforma (um campo aqui pode ser US mesmo
// que a plataforma inteira não esteja em US_NUMBER_FORMAT_PLATFORMS).
export const US_NUMBER_FORMAT_FIELDS = new Set(['BTG:di_futuro', 'BTG:financial_value']);

// Layout de destino da colagem - igual para qualquer plataforma (o que muda
// de plataforma para plataforma é o BROKER_FIELD_MAPPING acima). issue_date e
// di_futuro não são required: quando a corretora não manda a coluna (ver
// mapeamentos acima), issue_date cai no default de hoje e di_futuro fica nulo.
export const ISSUANCE_HEADERS = [
    { label: 'Produto', field: 'product', required: true, format: 'text' },
    { label: 'Data Aplicação', field: 'issue_date', required: false, format: 'text' },
    { label: 'Vencimento', field: 'due_date', required: true, format: 'text' },
    { label: 'Indexador', field: 'indexador_raw', required: true, format: 'text' },
    { label: 'Taxa', field: 'tax', required: true, format: 'text' },
    // Taxa cheia/all-in/emissor/distribuidor - segunda taxa usada na conferência
    // de PU Desagiado (ver check_pu_desagiado.py no backend). Opcional: sem ela
    // a conferência fica indisponível, mas o cadastro em si não é bloqueado -
    // nem toda corretora manda essa coluna mapeada ainda.
    { label: 'Taxa Cheia', field: 'tax_gross', required: false, format: 'text' },
    { label: 'Quantidade', field: 'quantity', required: true, format: 'text' },
    // Não obrigatório na colagem: XP/NIKOS/AGORA/ITAU/MIRAE não mandam PU
    // de emissão (cheio) separado do desagiado - cai no default de 1000 em
    // toCreateItem (ver DEFAULT_PU_NOMINAL) quando a coluna não é mapeada.
    { label: 'PU', field: 'pu', required: false, format: 'text' },
    { label: 'PU Desagiado', field: 'pu_degassed', required: true, format: 'text' },
    // Não obrigatório na colagem (mesmo motivo do `pu` acima): NUBANK não manda
    // Valor/Financeiro em nenhuma das duas fontes reais (e-mail nem Excel anexo)
    // - toCreateItem calcula Quantidade × PU quando a coluna não é mapeada (ver
    // comentário lá). Bug real encontrado em 2026-09-02: com `required: true`
    // aqui, o CopyPasteTable bloqueava o Salvar ("Coluna Valor é obrigatória")
    // ANTES do cálculo rodar (esse cálculo só acontece no submit, dentro de
    // toCreateItem - a validação por coluna roda antes, em cima do dado cru).
    { label: 'Valor', field: 'financial_value', required: false, format: 'text' },
    { label: 'DI Futuro', field: 'di_futuro', required: false, format: 'text' },
];

export const normalizePlatformName = (name: string): string => name.trim().toUpperCase();

export type PastedRow = Record<string, string>;

// Converte "DD/MM/AAAA" (planilha, padrão BR) ou "M/D/AAAA" (PicPay, formato
// americano - ver US_DATE_FORMAT_FIELDS) ou "AAAA-MM-DD" (já no formato da
// API) para o formato ISO exigido por docs/api-contract.md ("YYYY-MM-DD").
export const toIsoDate = (raw: string, useUsFormat: boolean): string => {
    const trimmed = (raw || '').trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    if (useUsFormat) {
        const usMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (usMatch) {
            const [, month, day, year] = usMatch;
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
    }
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) return formatDateFromBRToISO(trimmed);
    return trimmed;
};

export const todayIso = (): string => new Date().toISOString().slice(0, 10);

// PU de emissão (cheio) de LCA/CDB é, na prática, sempre 1000 nas amostras
// reais vistas (ver docs/context/business-context.md §8.5) - usado quando a
// corretora não manda essa coluna separada do PU desagiado (ex: XP).
export const DEFAULT_PU_NOMINAL = 1000;

// Algumas corretoras (ex: INTER) juntam produto e indexador na mesma coluna
// "ATIVO" (ex: "CDB PRE", "LCA PRE"), embora o indexador já venha separado na
// coluna INDEXADOR. Aqui extraímos só o código do produto (LCA/CDB), ignorando
// qualquer sufixo - sem isso "CDB PRE" nunca bate com o enum exato aceito pela API.
export const normalizeProduct = (raw: string): string => {
    const upper = (raw || '').trim().toUpperCase();
    const match = upper.match(/^(LCA|CDB)\b/);
    return match ? match[1] : upper;
};

// Toda corretora usa uma grafia diferente para indicar pré vs pós-fixado na
// mesma coluna (Toro/PicPay: "PRE"/"DI"; Daycoval/BTG: "PRE"/"CDI"; Genial:
// "PRÉ-FIXADO"/"CDI"; INTER: "LCA PRE"/"LCA POS", literalmente "POS" - único
// caso visto até agora, amostra real 2026-09-03). "DI" como indexador é
// convenção de mercado para papel indexado ao CDI, por isso mapeia pra
// remuneration_index="CDI" (ver docs/context/business-context.md secao 8.5).
// A INTER não manda o nome do índice separado (só "POS"/"PRE" na mesma coluna
// do rate_type) - mesma convenção CDI, confirmada pela coluna "DI" (curva CDI
// futura) só vir preenchida nas linhas POS da amostra real.
export const normalizeRateAndIndex = (raw: string): { rateType: RateType; remunerationIndex: string | null } => {
    const upper = (raw || '').trim().toUpperCase();
    if (upper.startsWith('PRE') || upper.includes('PRÉ')) {
        return { rateType: 'PRE', remunerationIndex: null };
    }
    if (upper === 'DI' || upper === 'POS') {
        return { rateType: 'POS', remunerationIndex: 'CDI' };
    }
    return { rateType: 'POS', remunerationIndex: upper || null };
};

export const toCreateItem = (row: PastedRow, platformId: string, platformName: string): CreateInstrumentItem => {
    const normalizedPlatform = normalizePlatformName(platformName);
    const useUsFormatIssueDate = US_DATE_FORMAT_FIELDS.has(`${normalizedPlatform}:issue_date`);
    const useUsFormatDueDate = US_DATE_FORMAT_FIELDS.has(`${normalizedPlatform}:due_date`);
    // Formato de número pode variar por campo dentro da mesma corretora (ex:
    // BTG - ver US_NUMBER_FORMAT_FIELDS), não só por corretora inteira (ex:
    // XP/PicPay - ver US_NUMBER_FORMAT_PLATFORMS). O flag de campo tem
    // prioridade sobre o de plataforma.
    const parseNumFor = (field: string) => (
        US_NUMBER_FORMAT_FIELDS.has(`${normalizedPlatform}:${field}`) || US_NUMBER_FORMAT_PLATFORMS.has(normalizedPlatform)
            ? currencyToNumberUs
            : currencyToNumber
    );
    const { rateType, remunerationIndex } = normalizeRateAndIndex(row.indexador_raw);
    // Genial manda "0" literal em vez de vazio na coluna CURVA quando o papel é
    // PRE (sem DI Futuro aplicável) - trata como ausência de valor, não como
    // taxa zero de fato.
    const parsedDiFuturo = parseNumFor('di_futuro')(row.di_futuro || '');
    const quantity = Math.round(parseNumFor('quantity')(row.quantity || ''));
    const pu = row.pu ? parseNumFor('pu')(row.pu) : DEFAULT_PU_NOMINAL;

    return {
        product: normalizeProduct(row.product) as InstrumentProduct,
        // Toro/Daycoval/INTER não mandam data de aplicação na planilha - cai no
        // default de hoje, editável depois (ver decisão do negócio em 2026-08-19).
        issue_date: row.issue_date ? toIsoDate(row.issue_date, useUsFormatIssueDate) : todayIso(),
        due_date: toIsoDate(row.due_date, useUsFormatDueDate),
        rate_type: rateType,
        remuneration_index: remunerationIndex,
        tax: parseNumFor('tax')(row.tax || ''),
        tax_gross: row.tax_gross ? parseNumFor('tax_gross')(row.tax_gross) : null,
        quantity,
        pu,
        pu_degassed: parseNumFor('pu_degassed')(row.pu_degassed || ''),
        // Sem coluna própria (ex: NUBANK, nem no e-mail nem no Excel anexo - só
        // Quantidade e PU Emissão), calcula Quantidade × PU nominal - mesma
        // convenção já confirmada matematicamente pra Toro/SANTCOR/Daycoval
        // (decisão do usuário, 2026-09-02).
        financial_value: row.financial_value ? parseNumFor('financial_value')(row.financial_value) : quantity * pu,
        di_futuro: parsedDiFuturo > 0 ? parsedDiFuturo : null,
        platform_id: platformId,
    };
};

// ---------------------------------------------------------------------------
// NIKOS — colagem em bloco ("memória de cálculo"), não tabular (2026-09-02)
// ---------------------------------------------------------------------------
//
// A Nikos não manda planilha com cabeçalho+linhas como as outras corretoras -
// manda um Excel por operação ("memória de cálculo"), em células fixas, sem
// tabela. O usuário cola o bloco inteiro (Ctrl+C na área de dados do Excel,
// Ctrl+V na tela) e cada colagem vira UMA linha nova na tabela (acumula, não
// substitui - ver `appendMode`/`parseBlockPaste` em CopyPasteTable.tsx).
//
// Confirmado em 5 arquivos reais (2026-08-31 e 2026-09-02, papéis PRE e POS)
// que o layout é sempre o mesmo:
//   - Bloco de dados: "Papel"/"Valor"/"P.U"/"Quantidade"/"Emissão"/
//     "Vencimento"/"Dias Uteis" - label e valor na MESMA linha, coluna
//     seguinte (varre a linha da label pra direita até achar a 1ª célula não
//     vazia - não assume coluna fixa, o bloco desloca uma coluna inteira
//     entre PRE e POS por causa da label extra "Taxa Referencial").
//   - Mini-tabela de taxas: "Taxa Cliente"/"Tx <corretora>" (cabeçalho) numa
//     linha, valores na MESMA coluna, linha seguinte. Só em papel POS existe
//     também "Taxa Referencial" (rótulo à esquerda de "Taxa Cliente"),
//     mesmo padrão vertical (valor uma linha abaixo, mesma coluna).
//   - O PU Desagiado do Excel deles NUNCA vem calculado (fórmula não
//     resolvida na exportação, confirmado nos 5 arquivos) - por isso
//     calculamos aqui, usando exatamente a mesma fórmula de duas taxas já
//     implementada e validada no backend (ver
//     src/domain/pu_desagiado_check.py do serviço funding e
//     docs/context/business-context.md §8.9), reproduzida em JS abaixo. O
//     "Dias Uteis" usado na fórmula é o que a própria Nikos já informa no
//     arquivo (D11) - não recalculamos dia útil no frontend.
// Números vêm em formato ambíguo (BR ou US, depende da config regional do
// Excel de quem copiou - confirmado: mesma célula com number_format
// "#,##0.00" mostrou "2,000.00" numa amostra e "1.141.000,00" noutra) - por
// isso usa parseAmbiguousDecimal (heurística: último separador = decimal),
// não currencyToNumber/currencyToNumberUs diretamente.

type CellGrid = string[][];
type CellPosition = { row: number; col: number };

const NIKOS_LABELS = {
    product: 'PAPEL',
    financialValue: 'VALOR',
    pu: 'P.U',
    quantity: 'QUANTIDADE',
    issueDate: 'EMISSÃO',
    dueDate: 'VENCIMENTO',
    businessDays: 'DIAS UTEIS',
    clientRate: 'TAXA CLIENTE',
    referenceRate: 'TAXA REFERENCIAL',
} as const;

const findLabelCell = (rows: CellGrid, label: string): CellPosition | null => {
    for (let row = 0; row < rows.length; row++) {
        for (let col = 0; col < rows[row].length; col++) {
            if ((rows[row][col] || '').trim().toUpperCase() === label) {
                return { row, col };
            }
        }
    }
    return null;
};

// Varre a MESMA linha da label, a partir da coluna seguinte, até achar a 1ª
// célula não vazia - usado pro bloco "Papel"/"Valor"/etc (label e valor lado
// a lado).
const valueRightOf = (rows: CellGrid, position: CellPosition): string | null => {
    const row = rows[position.row] || [];
    for (let col = position.col + 1; col < row.length; col++) {
        if ((row[col] || '').trim() !== '') return row[col].trim();
    }
    return null;
};

// Varre a MESMA coluna da label, a partir da linha seguinte, até achar a 1ª
// célula não vazia - usado pra mini-tabela "Taxa Cliente"/"Tx X"/"Taxa
// Referencial" (cabeçalho numa linha, valor na linha de baixo).
const valueBelowOf = (rows: CellGrid, position: CellPosition): string | null => {
    for (let row = position.row + 1; row < rows.length; row++) {
        const cell = (rows[row]?.[position.col] || '').trim();
        if (cell !== '') return cell;
    }
    return null;
};

// PU * (1+taxaCliente)^(du/252) / (1+taxaCheia)^(du/252) - mesma fórmula de
// src/domain/pu_desagiado_check.py::expected_pu_degassed_pre (taxas em %).
const computeNikosPreePuDegassed = (pu: number, taxClient: number, taxGross: number, businessDays: number): number => {
    const clientFactor = Math.pow(1 + taxClient / 100, businessDays / 252);
    const grossFactor = Math.pow(1 + taxGross / 100, businessDays / 252);
    return (pu * clientFactor) / grossFactor;
};

// G=(1+DIFuturo)^taxaCheia-1; H=(1+DIFuturo)^taxaCliente-1;
// PU=PUnominal/((1+G)/(1+H))^(du/252) - mesma fórmula de
// pu_desagiado_check.py::expected_pu_degassed_pos (taxas em % do CDI).
const computeNikosPosPuDegassed = (
    pu: number, taxClient: number, taxGross: number, diFuturo: number, businessDays: number,
): number => {
    const diRate = 1 + diFuturo / 100;
    const grossFactor = Math.pow(diRate, taxGross / 100) - 1;
    const clientFactor = Math.pow(diRate, taxClient / 100) - 1;
    const discount = Math.pow((1 + grossFactor) / (1 + clientFactor), businessDays / 252);
    return pu / discount;
};

// Retorna null quando o bloco colado não bate com o layout esperado (label
// obrigatória faltando) - CopyPasteTable trata isso como "não reconhecido" e
// não adiciona linha (ver onBlockParseError).
export const parseNikosBlock = (rows: CellGrid): PastedRow | null => {
    const productPos = findLabelCell(rows, NIKOS_LABELS.product);
    const financialValuePos = findLabelCell(rows, NIKOS_LABELS.financialValue);
    const puPos = findLabelCell(rows, NIKOS_LABELS.pu);
    const quantityPos = findLabelCell(rows, NIKOS_LABELS.quantity);
    const issueDatePos = findLabelCell(rows, NIKOS_LABELS.issueDate);
    const dueDatePos = findLabelCell(rows, NIKOS_LABELS.dueDate);
    const businessDaysPos = findLabelCell(rows, NIKOS_LABELS.businessDays);
    const clientRatePos = findLabelCell(rows, NIKOS_LABELS.clientRate);

    if (!productPos || !financialValuePos || !puPos || !quantityPos || !issueDatePos || !dueDatePos || !businessDaysPos || !clientRatePos) {
        return null;
    }

    const product = valueRightOf(rows, productPos);
    const financialValueRaw = valueRightOf(rows, financialValuePos);
    const puRaw = valueRightOf(rows, puPos);
    const quantityRaw = valueRightOf(rows, quantityPos);
    const issueDateRaw = valueRightOf(rows, issueDatePos);
    const dueDateRaw = valueRightOf(rows, dueDatePos);
    const businessDaysRaw = valueRightOf(rows, businessDaysPos);
    const taxClientRaw = valueBelowOf(rows, clientRatePos);
    // "Tx <corretora>" (Tx Nikos, Tx Órama...) é sempre a coluna seguinte a
    // "Taxa Cliente", mesma linha - o nome exato da corretora não importa
    // pra nós, só a posição relativa.
    const taxGrossRaw = valueBelowOf(rows, { row: clientRatePos.row, col: clientRatePos.col + 1 });

    if (!product || !financialValueRaw || !puRaw || !quantityRaw || !issueDateRaw || !dueDateRaw || !businessDaysRaw || !taxClientRaw || !taxGrossRaw) {
        return null;
    }

    // Só em papel POS existe "Taxa Referencial" (o CDI de referência do
    // período) - presença dela é o que diferencia PRE de POS nesse layout,
    // já que a Nikos não manda uma coluna "Indexador" separada.
    const referenceRatePos = findLabelCell(rows, NIKOS_LABELS.referenceRate);
    const isPos = referenceRatePos !== null;
    const diFuturoRaw = referenceRatePos ? valueBelowOf(rows, referenceRatePos) : null;
    if (isPos && !diFuturoRaw) return null;

    const pu = parseAmbiguousDecimal(puRaw);
    const quantity = Math.round(parseAmbiguousDecimal(quantityRaw));
    const financialValue = parseAmbiguousDecimal(financialValueRaw);
    const businessDays = parseAmbiguousDecimal(businessDaysRaw);
    // Taxas vêm como fração (0,9 = 90%) - domínio espera percentual (90).
    const taxClient = parseAmbiguousDecimal(taxClientRaw) * 100;
    const taxGross = parseAmbiguousDecimal(taxGrossRaw) * 100;
    const diFuturo = diFuturoRaw ? parseAmbiguousDecimal(diFuturoRaw) * 100 : null;

    const puDegassed = isPos
        ? computeNikosPosPuDegassed(pu, taxClient, taxGross, diFuturo as number, businessDays)
        : computeNikosPreePuDegassed(pu, taxClient, taxGross, businessDays);

    return {
        product,
        issue_date: toIsoDate(issueDateRaw, false),
        due_date: toIsoDate(dueDateRaw, false),
        indexador_raw: isPos ? 'DI' : 'PRE',
        tax: String(taxClient),
        tax_gross: String(taxGross),
        quantity: String(quantity),
        pu: String(pu),
        pu_degassed: String(puDegassed),
        financial_value: String(financialValue),
        di_futuro: diFuturo !== null ? String(diFuturo) : '',
    };
};
